import { describeEngine } from '@md/engine';
import { GAME_META } from '@md/game-magnetic-duo';
import {
  APP_VERSION,
  type ContentCatalog,
  catalogLevels,
  EMPTY_PROGRESS,
  MAX_STARS,
  type ProgressState,
  ROOM_CODE_LENGTH,
} from '@md/shared';
import { CONTROL_HINTS } from '../input/keyboard';
import type { Settings } from '../settings';
import { button, el } from '../ui/dom';

/**
 * Écran d'accueil (concept §8.3) : **une carte par jeu**.
 *
 * La V1 n'en livre qu'un — Magnetic Duo — mais la structure est celle du
 * multi-jeux : la liste `GAME_CARDS` est une donnée, les futurs jeux s'y
 * ajoutent (ils apparaissent « à venir » tant que leur module n'existe pas) et
 * l'accueil n'a aucun `if (gameId === …)`.
 *
 * PHASE 5 : la carte du jeu affiche la progression (étoiles gagnées), propose
 * de **reprendre** au premier niveau non terminé, et l'écran porte les
 * préférences son / vibration.
 */

/** Une carte de jeu de l'accueil. */
export interface GameCardDef {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** `true` quand le module existe réellement et que le jeu est jouable. */
  readonly available: boolean;
  /** Les deux pôles / symboles du duo, pour la vignette. */
  readonly poles: readonly [string, string];
}

/**
 * Catalogue des jeux de l'accueil.
 *
 * Les entrées « à venir » viennent du concept §8 : elles montrent que le
 * moteur est prêt à en accueillir d'autres, sans rien promettre de jouable.
 */
export const GAME_CARDS: readonly GameCardDef[] = Object.freeze([
  {
    id: GAME_META.id,
    name: GAME_META.name,
    description: GAME_META.description,
    available: true,
    poles: ['+', '−'],
  },
  {
    id: 'shadow-light',
    name: 'Ombre & Lumière',
    description: 'Un porteur de lampe et une ombre qui ne vit que dans le noir. À venir.',
    available: false,
    poles: ['☀', '☾'],
  },
  {
    id: 'ghost-alive',
    name: 'Fantôme & Vivant',
    description: 'L’un traverse les murs, l’autre les pousse. À venir.',
    available: false,
    poles: ['👻', '🧍'],
  },
]);

/** Session de compte affichée sur l'accueil (`null` = pas connecté). */
export type HomeAccountState = { readonly pseudo: string } | null;

export interface HomeOptions {
  /** Lance une partie locale sur le premier niveau du catalogue. */
  readonly onPlay: () => void;
  /** Reprend au premier niveau non terminé. */
  readonly onContinue: () => void;
  /** Ouvre le sélecteur de niveaux en mode local. */
  readonly onBrowseLevels: () => void;
  /** Crée une room réseau et bascule sur le lobby. */
  readonly onCreateRoom: () => void;
  /** Rejoint une room existante par son code. */
  readonly onJoinRoom: (code: string) => void;
  /** Change une préférence (son, vibration). */
  readonly onSettings: (patch: Partial<Settings>) => void;
  /** Préférences courantes. */
  readonly settings: Settings;
  /** Crée un compte (pseudonyme + mot de passe). */
  readonly onAccountRegister: (pseudo: string, password: string) => void;
  /** Ouvre une session sur un compte existant. */
  readonly onAccountLogin: (pseudo: string, password: string) => void;
  /** Ferme la session et repasse en progression locale. */
  readonly onAccountLogout: () => void;
}

export interface HomeScreen {
  readonly element: HTMLElement;
  /** Affiche une erreur de connexion / de code (`null` efface). */
  setError(message: string | null): void;
  /** Pré-remplit le champ de code (lien d'invitation `?join=CODE`). */
  setCode(code: string): void;
  /** Publie le catalogue chargé depuis l'API (compteur de niveaux, drafts). */
  setCatalog(catalog: ContentCatalog | null): void;
  /** Publie la progression du joueur (étoiles, reprise). */
  setProgress(progress: ProgressState): void;
  /** Publie la session de compte (affichage connecté / formulaire). */
  setAccount(state: HomeAccountState): void;
  /** Affiche une erreur de compte (`null` efface). */
  setAccountError(message: string | null): void;
}

export function renderHome(options: HomeOptions): HomeScreen {
  const engine = describeEngine();

  const view = el('main', 'screen screen--home');

  const header = el('header', 'hero');
  const title = el('h1', 'hero__title');
  title.append(el('span', 'hero__glyph', '🧲'), document.createTextNode('Magnetic Duo'));
  header.append(
    title,
    el('p', 'hero__tagline', 'Deux robots magnétiques. Une usine. Aucune sortie en solo.'),
  );

  // --- Carte du jeu jouable --------------------------------------------------
  const main = GAME_CARDS[0] as GameCardDef;
  const card = el('article', 'game-card');
  card.dataset.gameId = main.id;
  card.dataset.available = 'true';
  const poles = el('div', 'game-card__poles');
  poles.append(
    el('span', 'pole pole--north', main.poles[0]),
    el('span', 'pole pole--south', main.poles[1]),
  );

  const playButton = button('Jouer en local (2 joueurs)', 'play-local', 'button button--primary');
  playButton.addEventListener('click', () => options.onPlay());

  const continueButton = button('Reprendre la partie', 'continue-level', 'button button--primary');
  continueButton.hidden = true;
  continueButton.addEventListener('click', () => options.onContinue());

  const browseButton = button('Choisir un niveau', 'browse-levels', 'button');
  browseButton.addEventListener('click', () => options.onBrowseLevels());

  const createButton = button('Créer une partie en ligne', 'create-room', 'button');
  createButton.addEventListener('click', () => options.onCreateRoom());

  const codeInput = el('input', 'input');
  codeInput.type = 'text';
  codeInput.placeholder = 'CODE';
  codeInput.maxLength = ROOM_CODE_LENGTH;
  // `size` fixe la largeur intrinsèque du champ : sans lui, le défaut (20
  // caractères) impose au conteneur une largeur minimale qui fait déborder
  // l'accueil sur un viewport de téléphone.
  codeInput.size = ROOM_CODE_LENGTH;
  codeInput.autocapitalize = 'characters';
  codeInput.autocomplete = 'off';
  codeInput.spellcheck = false;
  codeInput.dataset.testid = 'join-code';
  codeInput.setAttribute('aria-label', 'Code de la partie');

  const joinButton = button('Rejoindre', 'join-submit', 'button');
  const submit = (): void => options.onJoinRoom(codeInput.value.trim().toUpperCase());
  joinButton.addEventListener('click', submit);
  codeInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  });

  const joinRow = el('div', 'join');
  joinRow.append(codeInput, joinButton);

  const error = el('p', 'join__error');
  error.dataset.testid = 'net-error';
  error.hidden = true;
  error.setAttribute('role', 'alert');

  const controls = el('ul', 'controls');
  for (const hint of CONTROL_HINTS) {
    const item = el('li', 'controls__item');
    item.append(el('strong', 'controls__who', hint.who), document.createTextNode(` ${hint.keys}`));
    controls.append(item);
  }

  const contentInfo = el('p', 'game-card__content');
  contentInfo.dataset.testid = 'content-summary';
  contentInfo.textContent = 'Chargement du contenu…';

  const starsInfo = el('p', 'game-card__stars');
  starsInfo.dataset.testid = 'total-stars';
  starsInfo.textContent = '';

  card.append(
    poles,
    el('h2', 'game-card__title', main.name),
    el('p', 'game-card__description', main.description),
    continueButton,
    playButton,
    browseButton,
    createButton,
    joinRow,
    error,
    starsInfo,
    contentInfo,
    controls,
  );

  // --- Cartes « à venir » ----------------------------------------------------
  const soon = el('section', 'game-list');
  soon.dataset.testid = 'upcoming-games';
  for (const game of GAME_CARDS.slice(1)) {
    const entry = el('article', 'game-card game-card--soon');
    entry.dataset.gameId = game.id;
    entry.dataset.available = 'false';
    const entryPoles = el('div', 'game-card__poles');
    entryPoles.append(
      el('span', 'pole pole--north', game.poles[0]),
      el('span', 'pole pole--south', game.poles[1]),
    );
    entry.append(
      entryPoles,
      el('h2', 'game-card__title', game.name),
      el('p', 'game-card__description', game.description),
      el('span', 'badge', 'à venir'),
    );
    soon.append(entry);
  }

  // --- Préférences -----------------------------------------------------------
  const prefs = el('div', 'prefs');
  const soundToggle = button('', 'toggle-sound', 'button button--ghost');
  const hapticsToggle = button('', 'toggle-haptics', 'button button--ghost');
  const paintPrefs = (settings: Settings): void => {
    soundToggle.textContent = settings.sound ? '🔊 Son activé' : '🔇 Son coupé';
    soundToggle.dataset.on = String(settings.sound);
    soundToggle.setAttribute('aria-pressed', String(settings.sound));
    hapticsToggle.textContent = settings.haptics ? '📳 Vibration activée' : '📴 Vibration coupée';
    hapticsToggle.dataset.on = String(settings.haptics);
    hapticsToggle.setAttribute('aria-pressed', String(settings.haptics));
  };
  let settings = options.settings;
  paintPrefs(settings);
  soundToggle.addEventListener('click', () => {
    settings = { ...settings, sound: !settings.sound };
    paintPrefs(settings);
    options.onSettings({ sound: settings.sound });
  });
  hapticsToggle.addEventListener('click', () => {
    settings = { ...settings, haptics: !settings.haptics };
    paintPrefs(settings);
    options.onSettings({ haptics: settings.haptics });
  });
  prefs.append(soundToggle, hapticsToggle);

  // --- Compte (optionnel) ----------------------------------------------------
  const accountSection = el('section', 'account');
  accountSection.dataset.testid = 'account';

  const accountStatus = el('p', 'account__status');
  accountStatus.dataset.testid = 'account-status';

  const pseudoInput = el('input', 'input');
  pseudoInput.type = 'text';
  pseudoInput.placeholder = 'Pseudonyme';
  pseudoInput.autocomplete = 'username';
  pseudoInput.spellcheck = false;
  pseudoInput.dataset.testid = 'account-pseudo';
  pseudoInput.setAttribute('aria-label', 'Pseudonyme');

  const passwordInput = el('input', 'input');
  passwordInput.type = 'password';
  passwordInput.placeholder = 'Mot de passe';
  passwordInput.autocomplete = 'current-password';
  passwordInput.dataset.testid = 'account-password';
  passwordInput.setAttribute('aria-label', 'Mot de passe');

  const registerButton = button('Créer un compte', 'account-register', 'button');
  registerButton.addEventListener('click', () =>
    options.onAccountRegister(pseudoInput.value, passwordInput.value),
  );
  const loginButton = button('Se connecter', 'account-login', 'button');
  const submitLogin = (): void => options.onAccountLogin(pseudoInput.value, passwordInput.value);
  loginButton.addEventListener('click', submitLogin);
  passwordInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitLogin();
    }
  });

  const logoutButton = button('Se déconnecter', 'account-logout', 'button button--ghost');
  logoutButton.addEventListener('click', () => options.onAccountLogout());

  const accountForm = el('div', 'account__form');
  const accountFields = el('div', 'account__fields');
  accountFields.append(pseudoInput, passwordInput);
  const accountActions = el('div', 'account__actions');
  accountActions.append(loginButton, registerButton);
  accountForm.append(accountFields, accountActions);

  const accountError = el('p', 'account__error');
  accountError.dataset.testid = 'account-error';
  accountError.hidden = true;
  accountError.setAttribute('role', 'alert');

  accountSection.append(
    el('h2', 'account__title', 'Compte'),
    accountStatus,
    accountForm,
    logoutButton,
    accountError,
  );

  const paintAccount = (state: HomeAccountState): void => {
    const connected = state !== null;
    accountSection.dataset.connected = String(connected);
    accountForm.hidden = connected;
    logoutButton.hidden = !connected;
    accountStatus.textContent = connected
      ? `Connecté : ${state.pseudo} — progression enregistrée en ligne.`
      : 'Un compte est nécessaire pour jouer : un pseudonyme, un mot de passe, et votre progression vous suit partout.';
    if (connected) {
      pseudoInput.value = '';
      passwordInput.value = '';
    }
    // Le compte est OBLIGATOIRE : sans session, rien n'est jouable.
    card.dataset.locked = String(!connected);
    for (const control of [
      playButton,
      continueButton,
      browseButton,
      createButton,
      joinButton,
      codeInput,
    ]) {
      control.disabled = !connected;
    }
  };
  paintAccount(null);

  const footer = el('footer', 'footer');
  // La version affichée est celle de `@md/shared` — la même que celle renvoyée
  // par `/healthz`, sans variable d'environnement au milieu.
  const version = el('span', 'footer__item', `v${APP_VERSION}`);
  version.dataset.testid = 'app-version';
  version.dataset.version = APP_VERSION;
  footer.append(
    version,
    el('span', 'footer__item', `moteur ${engine.name}`),
    el('span', 'footer__item', `${engine.tickRate} Hz`),
    el('span', 'footer__item', `dt ${(engine.tickDt * 1000).toFixed(2)} ms`),
  );

  // Le compte étant obligatoire, sa section précède tout le reste.
  view.append(header, accountSection, card, prefs, soon, footer);

  let catalog: ContentCatalog | null = null;
  let progress: ProgressState = EMPTY_PROGRESS;

  const paintProgress = (): void => {
    const levels = catalog ? catalogLevels(catalog) : [];
    const published = levels.filter((level) => level.status === 'published');
    const earned = Object.values(progress.levels).reduce((total, entry) => total + entry.stars, 0);
    const maximum = published.length * MAX_STARS;
    const completed = published.filter(
      (level) => (progress.levels[level.id]?.stars ?? 0) > 0,
    ).length;
    starsInfo.dataset.stars = String(earned);
    starsInfo.dataset.maxStars = String(maximum);
    starsInfo.dataset.completed = String(completed);
    starsInfo.textContent =
      maximum === 0
        ? ''
        : `⭐ ${earned}/${maximum} étoiles · ${completed}/${published.length} niveaux terminés`;
    continueButton.hidden = completed === 0 || completed === published.length;
  };

  return {
    element: view,
    setError(message: string | null): void {
      error.hidden = message === null;
      error.textContent = message ?? '';
    },
    setCode(code: string): void {
      codeInput.value = code;
    },
    setCatalog(next: ContentCatalog | null): void {
      catalog = next;
      if (!next) {
        contentInfo.textContent = 'Chargement du contenu…';
        contentInfo.dataset.levels = '';
        paintProgress();
        return;
      }
      const levels = catalogLevels(next);
      const drafts = levels.filter((level) => level.status === 'draft').length;
      contentInfo.dataset.levels = String(levels.length);
      contentInfo.dataset.drafts = String(drafts);
      contentInfo.textContent =
        `${levels.length} niveau(x) · ${next.worlds.length} world(s)` +
        (next.includesDrafts ? ` · ${drafts} brouillon(s) visibles` : '');
      paintProgress();
    },
    setProgress(next: ProgressState): void {
      progress = next;
      paintProgress();
    },
    setAccount(state: HomeAccountState): void {
      paintAccount(state);
    },
    setAccountError(message: string | null): void {
      accountError.hidden = message === null;
      accountError.textContent = message ?? '';
    },
  };
}
